import React, { FC, useEffect, useState, useContext } from "react";
import {
  message,
  Spin,
  Upload,
  UploadFile,
  UploadProps,
  Modal,
  Button,
} from "antd";
import {
  DeleteOutlined,
  PlayCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { RcFile } from "antd/lib/upload";
import { useCreate } from "@refinedev/core";
import { errorNotification, successNotification } from "../../helpers";
import { EnvContext } from "../../providers";

export enum EAudioFileTypes {
  COMMENTATORS = "MATCH",
}

interface IAppAudioUploaderProps {
  uploadProps?: Partial<UploadProps>;
  disabled?: boolean;
  entityId?: string;
  fileReset?: boolean;
  setDisabled?: (a: boolean) => void;
  serviceType: EAudioFileTypes;
  type: "MATCH";
  maxFileSizeMB?: number;
}

export const AppAudioUploader: FC<IAppAudioUploaderProps> = ({
  uploadProps,
  entityId,
  fileReset,
  serviceType,
  setDisabled = (value: boolean) => null,
  disabled,
  type,
  maxFileSizeMB = 10,
  ...props
}) => {
  const { value, onChange } = props as any;
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [previewAudio, setPreviewAudio] = useState<string>("");
  const { mutate } = useCreate();
  const [uploading, setUploading] = useState(false);
  const { THUMBOR_URL } = useContext(EnvContext);

  useEffect(() => {
    if (fileReset) {
      setFiles([]);
    }
  }, [fileReset]);

  useEffect(() => {
    if (value) {
      // Формируем полный URL с THUMBOR_URL для аудио
      const audioUrl = value.startsWith("http")
        ? value
        : `${THUMBOR_URL}${value}`;

      setFiles([
        {
          url: audioUrl,
          name: value.split("/").pop() || "audio-file.mp3",
          uid: Math.random().toString(),
        },
      ]);
    } else {
      setFiles([]);
    }
  }, [value, THUMBOR_URL]);

  const validateFileSize = (file: File, maxSizeMB: number) => {
    return new Promise<string>((resolve, reject) => {
      const fileSizeInMB = file.size / 1024 / 1024;
      if (fileSizeInMB > maxSizeMB) {
        reject(`Размер аудио файла не должен превышать ${maxSizeMB} MB`);
      } else {
        resolve("");
      }
    });
  };

  const validateType = (file: File) => {
    return new Promise<string>((resolve, reject) => {
      if (!["audio/mpeg", "audio/mp3"].includes(file.type)) {
        reject("Аудио файл может быть только в формате MP3");
      } else {
        resolve("");
      }
    });
  };

  const onUpdateForm = (file: string) => {
    onChange(!file ? undefined : file);
  };

  const onBeforeUpload = async (file: RcFile) => {
    try {
      await validateType(file);
      await validateFileSize(file, maxFileSizeMB);

      setUploading(true);
      setDisabled(true);

      mutate({
        dataProviderName: "uploadDataProvider",
        values: {
          entityId,
          serviceType,
          blob: file,
          type,
        },
        ...errorNotification({
          errorData: {
            callback() {
              setUploading(false);
              setDisabled(false);
            },
          },
        }),
        ...successNotification({
          callback: (data: { s3id: string }) => {
            const resultFile = [...files, { ...file, originFileObj: file }];
            setUploading(false);
            setDisabled(false);
            setFiles(resultFile);
            data && onUpdateForm(data.s3id);
          },
          successData: {
            message: "Аудио файл успешно загружен",
            type: "success",
          },
        }),
        resource: "",
      });

      return false;
    } catch (error: any) {
      message.error(error);
      setUploading(false);
      setDisabled(false);
      return false;
    }
  };

  const dummyRequest = (action: any) => {
    setTimeout(() => {
      action.onSuccess("ok");
    });
  };

  const onRemove = (file: UploadFile) => {
    const clearData = files.filter((stateFile) => stateFile.uid !== file.uid);
    setFiles(clearData);
    onUpdateForm("");
  };
  const customItemRender = (
    originNode: React.ReactElement,
    file: UploadFile
  ) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          padding: "8px 12px",
          border: "1px solid #d9d9d9",
          borderRadius: "6px",
          marginBottom: "8px",
          background: "#fafafa",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", flex: 1 }}>
          <PlayCircleOutlined
            style={{ fontSize: "16px", color: "#1890ff", marginRight: "8px" }}
          />
          <span
            style={{
              fontSize: "14px",
              color: "#333",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {file.name}
          </span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            type="text"
            danger
            size="small"
            onClick={() => onRemove(file)}
            icon={<DeleteOutlined />}
          >
            Удалить
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {uploading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <Spin />
        </div>
      )}

      <Upload
        disabled={disabled}
        customRequest={dummyRequest}
        accept=".mp3,audio/mpeg"
        fileList={files}
        beforeUpload={onBeforeUpload}
        onRemove={onRemove}
        itemRender={customItemRender}
        showUploadList={false}
        {...uploadProps}
      >
        {files?.length < 1 && (
          <Button icon={<UploadOutlined />} disabled={disabled}>
            {uploadProps?.children || "Загрузить аудио"}
          </Button>
        )}
      </Upload>

      {files?.length > 0 && customItemRender(<div />, files[0])}
    </div>
  );
};
